import React, { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
    CardBody,
    Table,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionBody
} from 'reactstrap'
import { Spinner } from 'react-bootstrap'
import { API_URL } from './utils/Constants/URLS'
import html2canvas from 'html2canvas'

// Helper: format money
const money = n =>
    (Number.isFinite(+n) ? Number(n) : 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })

// Helper: format date/time
const formatDateTime = iso => {
    if (!iso) return '-'
    try {
        const d = new Date(iso)
        return d.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        })
    } catch {
        return iso
    }
}

const FocViewInvoice = () => {
    const { sonumber, storeid } = useParams()
    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [openId, setOpenId] = useState('')
    const [pageNumber, setPageNumber] = useState(1)
    const token = localStorage.getItem('token')

    useEffect(() => {
        const fetchInvoices = async () => {
            try {
                setLoading(true)
                const res = await fetch(`${API_URL}foc/invoice?pagination=false`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
                const result = await res.json()
                const rawData = result?.data?.docs || []
                const data = Array.isArray(rawData) ? rawData : (rawData?.docs || [])

                // Only filter if params are provided
                if (sonumber || storeid) {
                    const filtered = data.filter(inv => {
                        // Filter by SO Number
                        if (sonumber) {
                            const invSoNum = inv.salesOrderNumber ||
                                inv.pickList?.salesOrderNumber ||
                                inv.pickList?.deliveryInstruction?.salesOrderNumber ||
                                inv.pickList?.deliveryInstruction?.salesOrder?.salesOrderNumber
                            return invSoNum === sonumber
                        }
                        // Filter by Store ID
                        if (storeid) {
                            const invStoreId = inv.store?._id || inv.store ||
                                inv.pickList?.store?._id || inv.pickList?.store
                            return String(invStoreId) === String(storeid)
                        }
                        return true
                    })
                    setInvoices(filtered)
                } else {
                    // No params, show all invoices
                    setInvoices(data)
                }
            } catch (e) {
                console.error('Error fetching FOC Invoices:', e)
                setError(e.message || 'Failed to fetch invoices')
            } finally {
                setLoading(false)
            }
        }
        fetchInvoices()
    }, [sonumber, storeid, token])

    const toggleAccordion = id => {
        setOpenId(openId === id ? '' : id)
    }

    // Compute grand total of all invoices
    const summaryTotals = useMemo(() => {
        const total = invoices.reduce(
            (acc, inv) => acc + (Number(inv.totalInvoiceValue) || 0),
            0
        )
        return { total }
    }, [invoices])

    const renderItemLines = invoice => {
        const items =
            invoice?.gstzenRequestPayload?.ItemList &&
                Array.isArray(invoice.gstzenRequestPayload.ItemList)
                ? invoice.gstzenRequestPayload.ItemList
                : []
        const pickItems = invoice?.pickList?.lineItems || []

        if (items.length === 0) {
            return (
                <tr>
                    <td colSpan='11' className='text-center py-3'>
                        No item details available
                    </td>
                </tr>
            )
        }

        return items.map((it, idx) => {
            const narration = pickItems[idx]?.narration || ''
            const uom =
                it.Unit ||
                pickItems[idx]?.model?.uom ||
                pickItems[idx]?.uom ||
                '-'
            return (
                <tr key={idx}>
                    <td>{it.SlNo ?? idx + 1}</td>
                    <td>
                        {it.PrdDesc ?? '-'}
                        {narration ? (
                            <>
                                <br />({narration})
                            </>
                        ) : null}
                    </td>
                    <td>{it.HsnCd ?? '-'}</td>
                    <td>{uom === 'Project' ? 'Nos' : uom === 'NA' ? ' ' : uom}</td>
                    <td>{it.Qty ?? 0}</td>
                    <td>{money(it.UnitPrice)}</td>
                    <td>{money(it.TotAmt)}</td>
                    <td>{money(it.Discount)}</td>
                    <td>{`${Number(it.GstRt || 0).toFixed(2)}%`}</td>
                    <td>{money(it.AssAmt)}</td>
                    <td>{money(it.TotItemVal)}</td>
                </tr>
            )
        })
    }

    const handleDownloadFocInvoice = async inv => {
        const gstReq = inv.gstzenRequestPayload || {}

        const seller = gstReq.SellerDtls || {}
        const buyer = gstReq.BuyerDtls || {}
        const ship = gstReq.ShipDtls || gstReq.DispDtls || {}
        const val = gstReq.ValDtls || {}
        const items = Array.isArray(gstReq.ItemList) ? gstReq.ItemList : []
        const pickItems = inv.pickList?.lineItems || []
        const poNumber = inv?.salesOrder?.customerPONumber || inv?.salesOrder?.PODetails
        const billingAdd = (buyer?.Loc && buyer?.Pin)
            ? `${buyer.Loc}`
            : (buyer?.Addr1 || '-')
        const shippingAdd =
            `${gstReq?.ShipDtls?.Addr1 || ''}` || '-'
        const poDate = inv.salesOrder?.customerPODate
        const soNo = inv.pickList?.salesOrderNumber || inv.salesOrder?.salesOrderNumber || '-'
        const priceBasis = inv.salesOrder?.priceBasis || '-'
        const isIGST = Number(val?.IgstVal || inv?.igstAmount || 0) > 0

        // Group items by HSN code for GST breakup
        const groupedByHsn = items.reduce((acc, it) => {
            const hsn = it.HsnCd ? String(it.HsnCd).trim() : '-'
            if (!acc[hsn]) {
                acc[hsn] = {
                    hsnCode: hsn,
                    taxableValue: 0,
                    cgstAmt: 0,
                    sgstAmt: 0,
                    igstAmt: 0,
                    gstRate: it.GstRt || 0
                }
            }
            const itemTotal = it.AssAmt
            acc[hsn].taxableValue += itemTotal
            acc[hsn].cgstAmt += Number(it.CgstAmt || 0)
            acc[hsn].sgstAmt += Number(it.SgstAmt || 0)
            acc[hsn].igstAmt += Number(it.IgstAmt || 0)
            return acc
        }, {})

        const hsnGroups = Object.values(groupedByHsn)

        // Helper function to generate header HTML (Delivery Challan — no e-invoice block)
        const generateHeader = () => `
        <div style="border:1px solid #000; background: white;">
          <!-- Header -->
          <div style="text-align:center; padding:10px 20px;">
            <center>
              <h2 style="margin:5px 0">Delivery Challan</h2>
            </center>
          </div>

          <!-- Seller / Challan Details -->
          <div style="display:flex; border-bottom:1px solid #000; border-top:1px solid #000;">
            <div style="width:40%; padding:8px; border-right:1px solid #000">
              <div style="font-size:12px; font-weight:bold">${String(
                seller.LglNm || '-'
            )}</div>
              <div style="font-size:12px">${String(seller.Addr1 || '')}</div>
              <div style="font-size:12px">GSTIN: ${String(
                seller.Gstin || '-'
            )}</div>
              <div style="font-size:12px">CIN: U74140WB2011PTC159722</div>
              <div style="font-size:12px">UDYAM Registration no: UDYAM-WB-09-0000238</div>
            </div>

            <div style="width:60%; padding:8px">
              <table style="width:100%; border-collapse:collapse">
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Challan No</td>
                <td style="padding:2px 4px; font-size:12px">${String(
                gstReq?.DocDtls?.No || inv.invoiceNumber || '-'
            )}</td></tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Challan Date</td><td style="padding:2px 4px; font-size:12px">${String(
                gstReq?.DocDtls?.Dt || '-'
            )}</td></tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Customer PO No.</td><td style="padding:2px 4px; font-size:12px">${String(
                poNumber || '-'
            )}</td></tr>
                <tr>
                  <td style="font-weight:bold; padding:2px 4px; font-size:12px">Customer PO Date</td>
                  <td style="padding:2px 4px; font-size:12px">
                    ${poDate ? new Date(poDate).toLocaleDateString('en-GB') : '-'}
                  </td>
                </tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">SO Id</td><td style="padding:2px 4px; font-size:12px">${String(
                soNo || '-'
            )}</td></tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Price Basis</td><td style="padding:2px 4px; font-size:12px">${String(
                priceBasis || '-'
            )}</td></tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Dispatch Doc No.</td><td style="padding:2px 4px; font-size:12px">${String(
                inv.dispatchDocumentNumber || '-'
            )}</td></tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Dispatched Through</td><td style="padding:2px 4px; font-size:12px">${String(
                inv.dispatchedThrough || '-'
            )}</td></tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Vehicle Details</td><td style="padding:2px 4px; font-size:12px">${String(
                inv.vehicleNumber || '-'
            )}</td></tr>
                <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Other References</td><td style="padding:2px 4px; font-size:12px">${String(
                inv.otherReferences || '-'
            )}</td></tr>
              </table>
            </div>
          </div>

          <!-- Consignee / Buyer -->
          <div style="display:flex; border-bottom:1px solid #000">
            <div style="width:50%; padding:8px">
              <div style="font-size:12px; font-weight:bold">Bill To</div>
              <div style="font-size:12px">${String(buyer.LglNm || '-')}</div>
              <div style="font-size:12px">${String(billingAdd || '-')}</div>
              <div style="font-size:12px">State Code: ${String(buyer.Stcd || '-')}</div>
              <div style="font-size:12px">GSTIN: ${String(buyer.Gstin || '-')}</div>
            </div>

            <div style="width:50%; padding:8px; border-left:1px solid #000">
              <div style="font-size:12px; font-weight:bold">Consignee (Ship To)</div>
              <div style="font-size:12px">${String(ship.LglNm || '-')}</div>
              <div style="font-size:12px">${String(shippingAdd || '-')}</div>
              <div style="font-size:12px">State Code: ${String(ship.Stcd || '-')}</div>
              <div style="font-size:12px">GSTIN: ${String(ship.Gstin || '-')}</div>
            </div>
          </div>
        </div>
      `

        // Create a temporary container for rendering
        const container = document.createElement('div')
        container.style.cssText = `
          position: absolute;
          left: -9999px;
          top: 0;
          width: 794px;
          padding: 0;
          margin: 0;
          background: white;
          box-sizing: border-box;
        `
        document.body.appendChild(container)

        try {
            // Import jsPDF dynamically if not already available
            const { jsPDF } = window.jspdf || (await import('jspdf'))
            const pdf = new jsPDF('p', 'mm', 'a4')

            const pageHeight = 297 // A4 height in mm
            const pageWidth = 210 // A4 width in mm
            const margin = 5

            // Calculate items per page (estimate based on row height)
            const itemsPerPage = 15
            const totalPages = Math.ceil(items.length / itemsPerPage) || 1

            for (let pageNum = 0; pageNum < totalPages; pageNum++) {
                const startIdx = pageNum * itemsPerPage
                const endIdx = Math.min(startIdx + itemsPerPage, items.length)
                const pageItems = items.slice(startIdx, endIdx)
                const isLastPage = pageNum === totalPages - 1

                // Build HTML for this page
                const pageHTML = `
                <div style="font-family: Arial; width: 100%; background: white; padding: 0; margin: 0; box-sizing: border-box;">
                  ${generateHeader()}
                  
                  <!-- Items Table -->
                  <div style="border:1px solid #000; border-top:none; padding:0; margin:0;">
                    <table style="width:100%; border-collapse:collapse;">
                      <thead>
                        <tr>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Sl</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Description</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">UOM</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">HSN</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Part Code</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Qty</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">GST%</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Rate</th>
                          <th style="padding:8px; font-size:12px; text-align:center; border-bottom:1px solid #000; background-color:white; color:black;">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${pageItems.map((it, idx) => {
                        const globalIdx = startIdx + idx
                        const narration = pickItems[globalIdx]?.narration || ''
                        const partCode = pickItems[globalIdx]?.model?.partCode || '-'
                        const uomRaw =
                            it.Unit ||
                            pickItems[globalIdx]?.model?.uom ||
                            pickItems[globalIdx]?.uom ||
                            ''
                        const uom =
                            uomRaw === 'Project'
                                ? 'Nos'
                                : uomRaw === 'NA'
                                    ? ' '
                                    : String(uomRaw || '-')
                        const productName = narration
                            ? `${String(it.PrdDesc || '-')} (${String(narration)})`
                            : String(it.PrdDesc || '-')

                        return `
                              <tr style="${idx === pageItems.length - 1 ? 'border-bottom:1px solid #000;' : ''}">
                                <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${globalIdx + 1}</td>
                                <td style="padding:4px; font-size:12px; border-right:1px solid #000;">${productName}</td>
                                <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${uom}</td>
                                <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.HsnCd || '-')}</td>
                                <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(partCode || '').match(/.{1,15}/g)?.join('<br>') || ''}</td>
                                <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${it.Qty === 0 ? '' : Number(it.Qty)}</td>
                                <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;"></td>
                                <td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;"></td>
                                <td style="padding:4px; font-size:12px; text-align:right;"></td>
                              </tr>
                            `
                    }).join('')}
                      </tbody>
                    </table>
                  </div>

                  ${isLastPage ? `
                  <!-- GST Breakup — only HSN shown (amounts blank as per Delivery Challan) -->
                  <div style="border:1px solid #000; border-top:none; padding:8px;">
                    ${isIGST ? `
                        <table border="1" style="width:100%; border-collapse:collapse">
                          <tr>
                            <th style="padding:4px; font-size:12px">HSN/SAC</th>
                            <th style="padding:4px; font-size:12px">Taxable Value</th>
                            <th style="padding:4px; font-size:12px">IGST Amt</th>
                            <th style="padding:4px; font-size:12px">Total Tax Amt</th>
                          </tr>
                          ${hsnGroups.map(group => `
                            <tr>
                              <td style="padding:4px; font-size:12px">${String(group.hsnCode)}</td>
                              <td style="padding:4px; font-size:12px"></td>
                              <td style="padding:4px; font-size:12px"></td>
                              <td style="padding:4px; font-size:12px"></td>
                            </tr>
                          `).join('')}
                        </table>
                      ` : `
                        <table border="1" style="width:100%; border-collapse:collapse">
                          <tr>
                            <th style="padding:4px; font-size:12px">HSN/SAC</th>
                            <th style="padding:4px; font-size:12px">Taxable Value</th>
                            <th style="padding:4px; font-size:12px">CGST Amt</th>
                            <th style="padding:4px; font-size:12px">SGST Amt</th>
                            <th style="padding:4px; font-size:12px">Total Tax Amt</th>
                          </tr>
                          ${hsnGroups.map(group => `
                            <tr>
                              <td style="padding:4px; font-size:12px">${String(group.hsnCode)}</td>
                              <td style="padding:4px; font-size:12px"></td>
                              <td style="padding:4px; font-size:12px"></td>
                              <td style="padding:4px; font-size:12px"></td>
                              <td style="padding:4px; font-size:12px"></td>
                            </tr>
                          `).join('')}
                        </table>
                      `}
                  </div>
                  
                  <!-- Declaration -->
                  <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between">
                    <div style="width:48%; border-right:1px solid #000; padding-right:10px;">
                      <b style="font-size:12px">Declaration</b><br/>
                      <span style="font-size:12px">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</span>
                    </div>

                    <div style="width:48%; padding-left:10px">
                      <b style="font-size:12px">Company's Bank Details</b><br/>
                      <span style="font-size:12px">Bank Name: <b>Union Bank of India</b></span><br/>
                      <span style="font-size:12px">A/C No: <b>607605010000127</b></span><br/>
                      <span style="font-size:12px">Branch & IFSC: <b>Bidhan Nagar Branch & UBIN0560766</b></span>
                    </div>
                  </div>

                  <!-- Signatures -->
                  <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between; align-items:flex-end; height:100px">
                    <div style="font-size:12px">
                      Customer Seal and Signature
                    </div>

                    <div style="text-align:right; font-size:12px">
                      Authorised Signatory
                    </div>
                  </div>
                  ` : ''}
                </div>
              `

                // Render to container
                container.innerHTML = pageHTML

                // Convert to canvas
                const canvas = await html2canvas(container, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                })

                // Add page if not first
                if (pageNum > 0) {
                    pdf.addPage()
                }

                // Add image to PDF
                const imgData = canvas.toDataURL('image/jpeg', 1.0)
                const imgWidth = pageWidth - 2 * margin
                let imgHeight = (canvas.height * imgWidth) / canvas.width

                // Constrain height to fit within page
                const maxHeight = pageHeight - 2 * margin
                if (imgHeight > maxHeight) {
                    imgHeight = maxHeight
                }

                pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight)
            }

            // Save PDF
            pdf.save(`foc-invoice-${inv.invoiceNumber || 'INV'}.pdf`)
        } finally {
            // Clean up
            document.body.removeChild(container)
        }
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading FOC Invoices...</p>
            </div>
        )
    }

    if (error) {
        return (
            <CardBody className='p-4'>
                <div className='alert alert-danger'>{error}</div>
            </CardBody>
        )
    }

    if (invoices.length === 0) {
        return (
            <CardBody className='p-4'>
                <p className="text-center p-3">No FOC Invoices found.</p>
            </CardBody>
        )
    }

    const itemsPerPage = 10
    const totalPages = Math.ceil(invoices.length / itemsPerPage) || 1
    const nextPage = pageNumber < totalPages ? pageNumber + 1 : null
    const prevPage = pageNumber > 1 ? pageNumber - 1 : null

    const paginatedList = invoices.slice((pageNumber - 1) * itemsPerPage, pageNumber * itemsPerPage)

    const getPaginationItems = () => {
        if (totalPages <= 4) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }
        return [1, 2, 3, '...', totalPages]
    }

    return (
        <div>
            <CardBody className='p-4'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                    <h5 className='mb-0 ml-1'>FOC Invoices</h5>
                    <div className='text-muted small'>
                        Total Invoice Value: <strong>₹ {money(summaryTotals.total)}</strong>
                    </div>
                </div>

                <Accordion
                    open={openId || ''}
                    toggle={toggleAccordion}
                    id='focViewInvoiceAccordion'
                >
                    {paginatedList.map(inv => {
                        const id = String(inv._id || inv.id)

                        const pickList = inv.pickList || {}
                        const salesOrder = inv.salesOrder || {}
                        const di = pickList.deliveryInstruction || {}
                        const gstzenReq = inv.gstzenRequestPayload || {}
                        const buyer = gstzenReq.BuyerDtls || {}
                        const seller = gstzenReq.SellerDtls || {}
                        const val = gstzenReq.ValDtls || {}

                        const invoiceNo = inv.invoiceNumber || gstzenReq?.DocDtls?.No || '-'
                        const invoiceDate = inv.invoiceDate || gstzenReq?.DocDtls?.Dt || null

                        const status = inv.status || '-'

                        const totalValue = Number(inv.totalInvoiceValue) || Number(val.TotInvVal) || 0
                        const taxable = Number(inv.taxableValue) || Number(val.AssVal) || 0
                        const igst = Number(inv.igstAmount) || Number(val.IgstVal) || 0
                        const cgst = Number(inv.cgstAmount) || Number(val.CgstVal) || 0
                        const sgst = Number(inv.sgstAmount) || Number(val.SgstVal) || 0

                        const hasError = !!inv.errorMessage || !!inv.gstzenResponse?.ErrorDetails?.length

                        return (
                            <AccordionItem key={id}>
                                <AccordionHeader targetId={id}>
                                    <div className='d-flex flex-column flex-md-row align-items-start gap-2 w-100'>
                                        <div className='me-auto'>
                                            <strong>Invoice:</strong> {invoiceNo}{' '}
                                            <span className='text-muted'>
                                                | SO: {pickList.salesOrderNumber || salesOrder.salesOrderNumber || '-'}
                                            </span>{' '}
                                            <span className='text-muted'>
                                                | Box: {pickList.boxNumber || '-'}
                                            </span>
                                        </div>

                                        <div className='text-muted small'>
                                            Date: {formatDateTime(invoiceDate)}
                                        </div>

                                        <div className='ms-md-3 d-flex gap-2 flex-wrap align-items-center'>
                                            <span
                                                className={`badge ${status === 'active'
                                                    ? 'bg-success'
                                                    : status === 'deleted'
                                                        ? 'bg-danger'
                                                        : 'bg-secondary'
                                                    }`}
                                            >
                                                Status: {status}
                                            </span>

                                            <span className='badge bg-light text-dark'>
                                                Total: ₹ {money(totalValue)}
                                            </span>
                                        </div>
                                    </div>
                                </AccordionHeader>

                                <AccordionBody accordionId={id}>
                                    {/* Top summary (Buyer / Seller) */}
                                    <div className='row mb-3'>
                                        <div className='col-md-6'>
                                            <h6>Seller Details</h6>
                                            <div className='small'>
                                                <div>
                                                    <strong>Name:</strong> {seller.LglNm || '-'}
                                                </div>
                                                <div>
                                                    <strong>GSTIN:</strong> {seller.Gstin || '-'}
                                                </div>
                                                <div>
                                                    <strong>Address:</strong> {seller.Addr1 || '-'}
                                                </div>
                                                <div>
                                                    <strong>Location:</strong> {seller.Loc || '-'} (
                                                    {seller.Stcd || '-'}) - {seller.Pin || '-'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className='col-md-6'>
                                            <h6>Buyer Details</h6>
                                            <div className='small'>
                                                <div>
                                                    <strong>Name:</strong> {buyer.LglNm || '-'}
                                                </div>
                                                <div>
                                                    <strong>GSTIN:</strong> {buyer.Gstin || '-'}
                                                </div>
                                                <div>
                                                    <strong>Address:</strong> {buyer.Addr1 || '-'}
                                                </div>
                                                <div>
                                                    <strong>Location:</strong> {buyer.Loc || '-'} (
                                                    {buyer.Stcd || '-'}) - {buyer.Pin || '-'}
                                                </div>
                                                <div>
                                                    <strong>Place of Supply (POS):</strong>{' '}
                                                    {buyer.Pos || '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Value summary */}
                                    <div className='row mb-3'>
                                        <div className='col-md-12'>
                                            <div className='table-responsive'>
                                                <Table className='table table-sm mb-0'>
                                                    <tbody>
                                                        <tr>
                                                            <th style={{ width: 200 }}>Taxable Value</th>
                                                            <td>₹ {money(taxable)}</td>
                                                            <th style={{ width: 200 }}>IGST</th>
                                                            <td>₹ {money(igst)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>CGST</th>
                                                            <td>₹ {money(cgst)}</td>
                                                            <th>SGST</th>
                                                            <td>₹ {money(sgst)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Total Invoice Value</th>
                                                            <td colSpan='3'>
                                                                <strong>₹ {money(totalValue)}</strong>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Error messages (if any) */}
                                    {hasError && (
                                        <div className='mb-3'>
                                            <div className='alert alert-warning small mb-1'>
                                                <strong>Errors:</strong>
                                            </div>
                                            <pre
                                                className='small bg-light p-2 rounded'
                                                style={{ whiteSpace: 'pre-wrap' }}
                                            >
                                                {inv.errorMessage || inv.gstzenResponse?.message || ''}
                                            </pre>

                                            {inv.gstzenResponse?.ErrorDetails &&
                                                inv.gstzenResponse.ErrorDetails.length > 0 && (
                                                    <ul className='small mb-0'>
                                                        {inv.gstzenResponse.ErrorDetails.map((e, i) => (
                                                            <li key={i}>
                                                                [{e.ErrorCode}] {e.ErrorMessage}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                        </div>
                                    )}

                                    {/* Item table */}
                                    <div className='table-responsive'>
                                        <Table
                                            className='table table-sm table-bordered mb-0'
                                            responsive
                                        >
                                            <thead className='table-light'>
                                                <tr>
                                                    <th style={{ width: 50 }}>Sl</th>
                                                    <th>Description</th>
                                                    <th style={{ width: 120 }}>HSN</th>
                                                    <th style={{ width: 80 }}>UOM</th>
                                                    <th style={{ width: 80 }}>Qty</th>
                                                    <th style={{ width: 120 }}>Unit Price</th>
                                                    <th style={{ width: 120 }}>Gross Amt</th>
                                                    <th style={{ width: 120 }}>Discount</th>
                                                    <th style={{ width: 80 }}>GST %</th>
                                                    <th style={{ width: 130 }}>Taxable</th>
                                                    <th style={{ width: 130 }}>Line Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>{renderItemLines(inv)}</tbody>
                                        </Table>
                                    </div>
                                    {(inv?.gstzenRequestPayload?.ItemList || []).length > 0 && (
                                        <div className='flex justify-end mt-2 gap-2'>
                                            <button
                                                className='btn add-btn text-white'
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    handleDownloadFocInvoice(inv)
                                                }}
                                            >
                                                Download Invoice
                                            </button>
                                        </div>
                                    )}
                                </AccordionBody>
                            </AccordionItem>
                        )
                    })}
                </Accordion>

                {/* Pagination: Prev 1 2 3 ... n Next */}
                {totalPages > 1 && (
                    <div className='d-flex justify-content-end mt-4'>
                        <nav aria-label='...'>
                            <ul className='pagination'>
                                <li className={`page-item ${!prevPage ? 'disabled' : ''}`}>
                                    <button
                                        className='page-link text-primary'
                                        onClick={() => prevPage && setPageNumber(prevPage)}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {getPaginationItems().map((item, i) =>
                                    item === '...' ? (
                                        <li key={`dots-${i}`} className='page-item disabled'>
                                            <span className='page-link'>...</span>
                                        </li>
                                    ) : (
                                        <li
                                            key={item}
                                            className={`page-item ${pageNumber === item ? 'active' : ''}`}
                                        >
                                            <button
                                                className={`page-link ${pageNumber === item ? 'text-white' : 'text-primary'}  `}
                                                onClick={() => setPageNumber(item)}
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    )
                                )}

                                <li className={`page-item ${!nextPage ? 'disabled' : ''}`}>
                                    <button
                                        className='page-link text-primary'
                                        onClick={() => nextPage && setPageNumber(nextPage)}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </CardBody>
        </div>
    )
}

export default FocViewInvoice
